import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom";
import Loader from "../../Components/Shared/Loader/loader";
import { useMsal } from "@azure/msal-react";
import WelcomeUser from "../../Components/Dashboard/Project/WelcomeUser";
import WorkspaceCreateProjectPage from "./Recommendation/WorkspaceCreateProjectPage";
import { AliceGPTContextProvider } from "../../Context/AliceGPT/AliceGPTContext";
import { useTranslation } from "react-i18next";
import { getUserAndItsOrganizations } from "../../API/Organization/organization";
import CreateProjectUsingAI from "../../Components/AIAgent/CreateProjectDialog";
import useInternalUserCheck from "../../Hooks/Misc/useInternalUserCheck";
import { Dialog } from "@progress/kendo-react-dialogs";
import { callApiWithAuthorization } from "../../API/CallApi";
import "./twoBotStyles.css";
import Scrollable from "../../Components/Shared/Edit/Scrollable";
import { getCurrentSubscriptionForOrganisation } from "../../API/Payment/Payment";
import Upgrade from "../../Components/Shared/Subscription/Upgrade";
import useGetProjects from "../../Hooks/Projects/useGetProjects";
import { postMessageToRuntime } from "../../Components/Helpers/Runtime/runtime";

export const WorkspaceCreateAppPage = (props) => {

    const params = useParams()
    const context = useMsal()

    const [state, setState] = useState({
        loading: true,
        organization: {},
        user: {},
        error: {
            isValid: true,
            type: "",
            message: ""
        }
    })

    const fileIdRef = React.useRef(null);

    const [selectedOption, setSelectedOption] = useState(null); // Default to null to show only the boxes initially
    const isInternalUser = useInternalUserCheck(params.userEmail);

    const [isTwoBotDialogOpen, setIsTwoBotDialogOpen] = useState(false);
    const [twoBotDescription, setTwoBotDescription] = useState("");
    const [twoBotErrorMessage, setTwoBotErrorMessage] = useState("");
    const [twoBotUploadedFile, setTwoBotUploadedFile] = useState(null);
    const [isTwoBotSubmitted, setIsTwoBotSubmitted] = useState(false); // Track if Two Bot dialog is submitted

    // Add subscription-related state
    const [subscription, setSubscription] = useState(null);
    const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
    const [projectsLoading, projects, projectsError] = useGetProjects(params.organizationId);

    const handleOptionChange = (option) => {
        if (option === "Two Bot") {
            postMessageToRuntime({
                eventType: "RECOMMENDATION_SKIPPED",
                value: true
            })
        }
        setSelectedOption(option);
    };

    const handleTwoBotDialogSubmit = async () => {
        const wordCount = twoBotDescription.trim().split(/\s+/).filter(Boolean).length;
        if (wordCount < 10 && !twoBotUploadedFile) {
            setTwoBotErrorMessage(t("please_enter_10_words_or_upload"));
            return;
        }

        // Check subscription limit before proceeding
        if (subscription && projects.length >= subscription.maxApps) {
            setShowUpgradeDialog(true);
            return;
        }

        setTwoBotErrorMessage("");
        setIsTwoBotDialogOpen(false);

        let fileId = null;

        if (twoBotUploadedFile) {
            const formData = new FormData();
            formData.append("file", twoBotUploadedFile);

            try {
                const response = await callApiWithAuthorization("/api/v0/ai/upload-file", {
                    method: "POST",
                    body: formData,
                });

                if (response.ok) {
                    const data = await response.json();
                    fileId = data.id; // Extract the file ID from the response
                } else {
                    console.error("File upload failed");
                }
            } catch (error) {
                console.error("Error uploading file:", error);
            }
        }

        fileIdRef.current = fileId; // Store the file ID in the ref
        setIsTwoBotSubmitted(true); // Set the boolean to true
    };

    const { t, i18n, ready: tReady } = useTranslation("workspace_create_app_page", { useSuspense: false });

    const validateAccess = async () => {
        let error = {
            isValid: true,
            type: "",
            message: ""
        }
        let organization = {}
        let user = {}
        const accessToken = localStorage.getItem("accessToken")
        if (!accessToken) {
            error.isValid = false
            error.type = "login"
            error.message = "You are not logged in"
        } else {
            let { organizations, user } = await getUserAndItsOrganizations(params.userEmail)
            
            organization = organizations.find(org => org._id === params.organizationId)
            if (user._id === undefined) {
                error.isValid = false
                error.type = "user"
                error.message = "User not found"
            } else if (organization.length === 0) {
                error.isValid = false
                error.type = "organization"
                error.message = "No organization found for User"
            } else if (organization._id !== params.organizationId) {
                error.isValid = false
                error.type = "organization"
                error.message = "You do not have access to this organization"
            } else if (!organization.isActive) {
                error.isValid = false
                error.type = "organization"
                error.message = "Organization is not active anymore"
            }
        }
        setState(prevState => ({
            ...prevState,
            error,
            loading: false,
            user,
            organization
        }))
    }

    // Fetch subscription data
    const fetchSubscription = async () => {
        if (state.organization._id) {
            try {
                const subscriptionData = await getCurrentSubscriptionForOrganisation(state.organization._id);
                setSubscription(subscriptionData);
            } catch (error) {
                console.error("Error fetching subscription:", error);
            }
        }
    };

    useEffect(() => {
        validateAccess();
    }, [context]);

    useEffect(() => {
        if (tReady) {
            i18n.changeLanguage(state.organization.platformLanguage || "en");
        }
    }, [tReady, state.organization])

    // Fetch subscription when organization is loaded
    useEffect(() => {
        if (state.organization._id) {
            fetchSubscription();
        }
    }, [state.organization._id]);

    if (state.loading) return <Loader />

    if (state.error.isValid === false && state.error.type === "subscription") {
        return (
            <WelcomeUser
                user={state.user}
                email={state.user.email}
                userRole={state.organization.roleType}
            />
        )
    }

    if (state.error.isValid === false) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center w-100 h-100">
                <div className="alert alert-danger" role="alert">
                    {state.error.message}
                </div>
            </div>
        )
    }

    if (selectedOption === null) {
        return (
            <div
                className="d-flex flex-column align-items-center"
                style={{
                    minHeight: "100vh",
                    background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
                }}
            >
                {/* Header with purple gradient and bubbles */}
                <div className="workspace-header-bubbles position-relative" style={{
                    background: "linear-gradient(135deg, #2563eb 0%,rgb(103, 74, 190) 100%)",
                    borderRadius: "18px 18px 0 0",
                    width: 900,
                    maxWidth: "98%",
                    minHeight: 140,
                    textAlign: "center",
                    fontWeight: 600,
                    fontSize: 24,
                    letterSpacing: "-0.01em",
                    position: "relative",
                    paddingBottom: 20,
                    overflow: "hidden",
                    marginTop: "5%"
                }}>
                    {/* Bubbles */}
                    <div className="bubble bubble-sm" style={{ top: 24, left: 80 }} />
                    <div className="bubble bubble-sm" style={{ top: 60, right: 100 }} />
                    <div className="bubble bubble-sm" style={{ bottom: 18, left: 140 }} />
                    <div className="bubble bubble-sm" style={{ bottom: 36, right: 60 }} />
                    {/* Logo */}
                    <div style={{ position: "relative", zIndex: 2, marginTop: 40, marginBottom: 8 }}>
                        <h5 style={{ color: "#ccc" }}>Codeless ONE Copilot</h5>
                    </div>
                    <div style={{ color: "#fff", position: "relative", zIndex: 2 }}>{t("ai_work_with_ai_title")}</div>
                    <div
                        style={{
                            margin: "12px auto 0 auto",
                            width: 60,
                            height: 5,
                            background: "#b6c3e6",
                            borderRadius: 3,
                            position: "relative",
                            zIndex: 2
                        }}
                    />
                </div>
                <div
                    className="d-flex justify-content-center align-items-stretch"
                    style={{
                        background: "white",
                        borderRadius: "0 0 18px 18px",
                        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
                        padding: "48px 24px",
                        width: 900,
                        maxWidth: "98%",
                        minHeight: 260
                    }}
                >
                    {/* AI Picks, You Choose */}
                    <div
                        className="me-3"
                        style={{
                            flex: 1,
                            background: "#fff",
                            borderRadius: 12,
                            border: "1px solid #e5e7eb",
                            cursor: "pointer",
                            padding: "24px 16px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            transition: "box-shadow 0.2s",
                            textAlign: "center"
                        }}
                        onClick={() => handleOptionChange("Normal")}
                    >
                        <div style={{
                            background: "#e0e7ff",
                            borderRadius: "50%",
                            width: 56,
                            height: 56,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: 16
                        }}>
                            <i className="fa-solid fa-wand-magic-sparkles" style={{ color: "#6366f1", fontSize: 28 }} />
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>{t("ai_picks_you_choose_title")}</div>
                        <div style={{ color: "#64748b", fontSize: 15, textAlign: "center", marginBottom: 12 }}>
                            {t("ai_picks_you_choose_desc")}
                        </div>
                        <span style={{
                            background: "#dcfce7",
                            color: "#22c55e",
                            borderRadius: 12,
                            fontSize: 14,
                            fontWeight: 500,
                            padding: "2px 14px"
                        }}>{t("ai_picks_you_choose_fastest")}</span>
                    </div>
                    {/* Two Bots Debate */}
                    <div
                        style={{
                            flex: 1,
                            background: "#fff",
                            borderRadius: 12,
                            border: "1px solid #e5e7eb",
                            cursor: "pointer",
                            padding: "24px 16px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            transition: "box-shadow 0.2s",
                            textAlign: "center"
                        }}
                        onClick={() => handleOptionChange("Two Bot")}
                    >
                        <div style={{
                            background: "#f3e8ff",
                            borderRadius: "50%",
                            width: 56,
                            height: 56,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: 16
                        }}>
                            <i className="fa-regular fa-person-military-to-person" style={{ color: "#a78bfa", fontSize: 28 }} />
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>{t("two_bots_debate_title")}</div>
                        <div style={{ color: "#64748b", fontSize: 15, textAlign: "center", marginBottom: 12 }}>
                            {t("two_bots_debate_desc")}
                        </div>
                        <span style={{
                            background: "#fef3c7",
                            color: "#f59e42",
                            borderRadius: 12,
                            fontSize: 14,
                            fontWeight: 500,
                            padding: "2px 14px"
                        }}>{t("two_bots_debate_slow")}</span>
                    </div>
                </div>
            </div>
        );
    }

    if (selectedOption === "Two Bot") {
        if (isTwoBotSubmitted) {
            return (
                <CreateProjectUsingAI
                    organizationId={state.organization._id}
                    user={state.user}
                    isAutoMode={true}
                    autoModeDescription={twoBotDescription}
                    autoModeFileId={fileIdRef.current}
                />
            )
        } else {
            return (
                <>
                    <Scrollable>
                        <div className="two-bot-container">
                            <div className="two-bot-card">
                                <h4 className="two-bot-title mb-2">{t("tell_us_about_your_app")}</h4>
                                <p className="two-bot-subtitle mb-0">{t("help_us_understand")}</p>
                                <div>
                                    <label className="form-label two-bot-label">{t("app_description_label")}</label>
                                    <textarea
                                        className="form-control two-bot-textarea"
                                        rows="5"
                                        placeholder={t("app_description_placeholder")}
                                        value={twoBotDescription}
                                        onChange={(e) => setTwoBotDescription(e.target.value)}
                                    />
                                    <div className="two-bot-wordcount mb-2">
                                        {t("word_count_minimum", { count: twoBotDescription.trim().split(/\s+/).filter(Boolean).length })}
                                    </div>
                                </div>
                                <div className="two-bot-divider d-flex align-items-center mb-3">
                                    <div className="two-bot-divider-line" />
                                    <span className="two-bot-divider-text">{t("or")}</span>
                                    <div className="two-bot-divider-line" />
                                </div>
                                <div className="two-bot-upload-box mb-4">
                                    <div className="two-bot-upload-icon">
                                        <i className="fa fa-cloud-arrow-up" />
                                    </div>
                                    <div className="two-bot-upload-title">{t("upload_requirements_title")}</div>
                                    <div className="two-bot-upload-desc">{t("upload_requirements_desc")}</div>
                                    <label htmlFor="twoBotFileUpload" className="btn btn-light" style={{ border: "1px solid #e5e7eb", fontWeight: 500 }}>
                                        <i className="fa fa-upload me-2" />{t("browse_files")}
                                        <input
                                            id="twoBotFileUpload"
                                            type="file"
                                            className="d-none"
                                            accept=".pdf,.doc,.docx"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    const allowedTypes = [
                                                        "application/pdf",
                                                        "application/msword",
                                                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                                    ];
                                                    if (!allowedTypes.includes(file.type)) {
                                                        setTwoBotErrorMessage(t("unsupported_file_type"));
                                                        setTwoBotUploadedFile(null);
                                                    } else {
                                                        setTwoBotUploadedFile(file);
                                                        setTwoBotErrorMessage("");
                                                    }
                                                }
                                            }}
                                        />
                                    </label>
                                    {twoBotUploadedFile && (
                                        <div className="two-bot-upload-file">{twoBotUploadedFile.name}</div>
                                    )}
                                </div>
                                {twoBotErrorMessage && (
                                    <div className="two-bot-error">
                                        {twoBotErrorMessage}
                                    </div>
                                )}
                                <div className="two-bot-actions d-flex justify-content-end gap-2 mb-2">
                                    <button
                                        className="btn btn-light me-2"
                                        onClick={() => setSelectedOption(null)}
                                        style={{ border: "1px solid #e5e7eb" }}
                                    >
                                        {t("cancel")}
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleTwoBotDialogSubmit}
                                    >
                                        {t("continue")}
                                    </button>
                                </div>
                            </div>
                            <div className="two-bot-tips mb-2">
                                <div className="two-bot-tips-title">
                                    <i className="fa fa-lightbulb me-2" style={{ color: "#2563eb" }} />{t("tips_for_great_description")}
                                </div>
                                <ul className="two-bot-tips-list">
                                    <li>
                                        <span className="text-success">
                                            <i className="fa fa-check" />
                                        </span>
                                        &nbsp;
                                        {t("tip_main_purpose")}
                                    </li>
                                    <li>
                                        <span className="text-success">
                                            <i className="fa fa-check" />
                                        </span>
                                        &nbsp;
                                        {t("tip_target_audience")}
                                    </li>
                                    <li>
                                        <span className="text-success">
                                            <i className="fa fa-check" />
                                        </span>
                                        &nbsp;
                                        {t("tip_key_features")}
                                    </li>
                                    <li>
                                        <span className="text-success">
                                            <i className="fa fa-check" />
                                        </span>
                                        &nbsp;
                                        {t("tip_technical_requirements")}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </Scrollable>
                    {showUpgradeDialog && (
                        <Upgrade
                            toggleDialog={() => setShowUpgradeDialog(false)}
                            organization={{ id: state.organization._id }}
                            Headline={'Need to build <br/>more apps? '}
                            Type={'Pro'}
                        />
                    )}
                </>
            );
        }
    }

    return (
        <AliceGPTContextProvider>
            {selectedOption === "Normal" ? (
                <WorkspaceCreateProjectPage
                    showBackButton={false}
                    organization={state.organization}
                    user={state.user}
                />
            ) : (
                <CreateProjectUsingAI
                    organizationId={state.organization._id}
                    user={state.user}
                    isInternalUser={false}
                />
            )}
        </AliceGPTContextProvider>
    );
}